import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import type { ParsedMigrationError } from '../migration-errors';

/**
 * Renders a server refusal in full: the plain-English reading, the server's own
 * sentence, and the machine-readable identifiers.
 *
 * ── WHAT THIS DELIBERATELY DOES NOT DO ─────────────────────────────────────
 * It never summarises the backend's reason away. `headline` is the readable
 * explanation, but `detail`, `gate` and `code` are rendered alongside it, so the
 * operator (and anyone they paste this to) always has the exact gate name the
 * server refused at. A refusal this build does not recognise is labelled as
 * unexpected rather than being dressed up as a known one.
 */
export function MigrationErrorNotice({
  error,
  testId,
}: {
  error: ParsedMigrationError;
  testId: string;
}) {
  return (
    <div
      className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3"
      data-testid={testId}
      role="alert"
    >
      <p className="flex items-start gap-2 text-sm font-medium text-destructive">
        <FontAwesomeIcon icon={faCircleExclamation} className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error.headline}</span>
      </p>

      {!error.recognised && (
        <p className="mt-1.5 pl-6 text-xs font-medium text-destructive/90">
          This is an unexpected refusal. The server&apos;s exact response is shown below.
        </p>
      )}

      {error.detail !== null && error.detail !== error.headline && (
        <p
          className="mt-1.5 break-words pl-6 text-xs text-muted-foreground"
          data-testid={`${testId}-detail`}
        >
          {error.detail}
        </p>
      )}

      {(error.gate !== null || error.code !== null || error.status !== null) && (
        <p
          className="mt-1.5 pl-6 font-mono text-xs text-muted-foreground"
          data-testid={`${testId}-code`}
        >
          {[error.status !== null ? `HTTP ${error.status}` : null, error.code, error.gate]
            .filter((x): x is string => typeof x === 'string' && x.length > 0)
            .join(' · ')}
        </p>
      )}
    </div>
  );
}
